// =========================================================
// * Vuetify Material Dashboard PRO - v2.1.0
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vuetify-material-dashboard-pro
// * Copyright 2019 Creative Tim (https://www.creative-tim.com)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

import Vue from 'vue'
import App from './App.vue'
import { PiniaVuePlugin } from 'pinia'
import pinia from '@/stores'
import '@/stores/oidc' // Effets de bord OIDC (callbacks oidcService + init) au démarrage
import './plugins/base'
import './plugins/vee-validate'
import vuetify from './plugins/vuetify'
import '@/styles/style.scss'
// Plugins
import plugins from '@/utils/plugins/index'
// i18n
import i18n from './i18n'
// Router — point d'entrée unifié
import router from '@/router'
// MSAL
import { EventType } from '@azure/msal-browser'
import msalPlugin from './plugins/msalPlugin'
import { msalInstance } from './config/msal'
import { clearStuckInteraction } from '@/msal/clearStuckInteraction'
import logger from '@/utils/logger'

// MSAL v3: Must call initialize() before any other MSAL API
// Then handleRedirectPromise() must be called ONCE at app startup
const initializeMsal = async () => {
  try {
    // MSAL v3 requires explicit initialization
    await msalInstance.initialize()

    // Handle redirect response (if returning from Azure AD login)
    const response = await msalInstance.handleRedirectPromise()
    if (response) {
      // User just logged in via redirect, set the account
      msalInstance.setActiveAccount(response.account)
    } else {
      // No redirect response, check for existing accounts
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0])
      }
    }
  } catch (error) {
    // Drapeau d'interaction resté bloqué (ex: logout interrompu) : on le purge
    // pour que l'utilisateur puisse de nouveau se connecter.
    if (error && error.errorCode === 'interaction_in_progress') {
      clearStuckInteraction()
    }
    logger.error('MSAL initialization error:', error)
  }
}

// Event callback for login success
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload
    const account = payload.account
    msalInstance.setActiveAccount(account)
  }
})

// Back/forward cache (bfcache) : quand la page est restaurée depuis le cache du
// navigateur (ex: l'utilisateur fait "Précédent" depuis la page d'auth Microsoft),
// le JS ne se réexécute pas et MSAL reste figé dans un état d'interaction en cours
// → le bouton de connexion ne répond plus. On force un rechargement propre pour
// réinitialiser entièrement l'état d'authentification.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    window.location.reload()
  }
})

// Authentification est 'msal'
Vue.use(msalPlugin, msalInstance)

Vue.config.productionTip = false
// $company, $fournisseur, $env → migré vers useAppConfig composable

// Pinia — coexiste avec Vuex pendant la migration progressive Vuex → Pinia.
// L'instance est créée dans `@/stores` pour être réutilisable hors composants.
Vue.use(PiniaVuePlugin)

// Plugins
Vue.use(plugins)

// MSAL utilise une iframe cachée pour renouveler les tokens silencieusement
// (acquireTokenSilent). Comme redirectUri = '/', cette iframe chargerait TOUTE l'app :
// double initialisation MSAL, timers d'auto-logout en double, et surtout écrasement du
// drapeau `interaction.status` dans le localStorage PARTAGÉ → conflits d'interaction et
// bouton de connexion bloqué. On ne monte donc pas l'app dans une iframe ; le parent lit
// le hash de l'iframe de lui-même.
if (window.self !== window.top) {
  logger.info('MSAL: contexte iframe détecté — application non montée')
} else {
  // Initialize MSAL first, then start Vue app
  initializeMsal().then(() => {
    new Vue({
      router,
      pinia,
      vuetify,
      i18n,
      render: h => h(App),
    }).$mount('#app')
  })
}
